import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";

import { useLocalStorage } from "../../../../../shared/hooks/useLocalStorage";
import { RequirementTableItemData } from "../../../types";

export const IDENTITY_VERIFICATION_REQUIREMENT_EVENT = {
  StartVerification: "StartVerification",
  CompleteVerification: "CompleteVerification"
} as const;

/** The non-terminal request status — the same single stage v1 had. */
export const IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES: (
  | BiometricIdentityVerificationRequestStatus
  | undefined
)[] = [BiometricIdentityVerificationRequestStatus.PENDING];

const getKey = (
  requirement: RequirementTableItemData,
  identityVerification: BiometricIdentityVerificationRequest | undefined
): string => {
  return `${requirement.dataSourceId} ${identityVerification?.id}`;
};

export const useIDVPendingSuccessStatus = () => {
  const [pendingSuccessRequirements, setPendingSuccessRequirements] =
    useLocalStorage<Record<string, Date>>(
      "identityVerificationPendingSuccessRequirements",
      {}
    );

  const setPendingSuccessStatus = useCallback(
    (
      requirement: RequirementTableItemData,
      identityVerification: BiometricIdentityVerificationRequest | undefined
    ) => {
      setPendingSuccessRequirements(prevState => ({
        ...prevState,
        [getKey(requirement, identityVerification)]: new Date()
      }));
    },
    [setPendingSuccessRequirements]
  );

  const getIsPendingSuccessStatus = useCallback(
    (
      requirement: RequirementTableItemData,
      identityVerification: BiometricIdentityVerificationRequest | undefined
    ) => {
      const pendingSuccessStartedAt =
        pendingSuccessRequirements[getKey(requirement, identityVerification)];

      const timeDiff = pendingSuccessStartedAt
        ? Date.now() - new Date(pendingSuccessStartedAt).getTime()
        : 0;

      const FiveMinutes = 5 * 60 * 1000;

      return (
        pendingSuccessStartedAt &&
        IDENTITY_VERIFICATION_IN_FLIGHT_STATUSES.includes(
          identityVerification?.status
        ) &&
        timeDiff < FiveMinutes
      );
    },
    [pendingSuccessRequirements]
  );

  return {
    setPendingSuccessStatus,
    getIsPendingSuccessStatus
  };
};
